import type { ProductionItem, ProductionStage, StageViolation } from '@qlk/shared';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { AlertTriangle, PenLine } from 'lucide-react';
import { useMemo, useState } from 'react';

import { coQuyen, useDuongDanTrang, useToi } from '../../../lib/auth.js';
import {
  EmptyState,
  KhoiChuaCoKenh,
  Nut,
  OChon,
  PageHeader,
  ToolbarBang,
} from '../../../ui/index.js';
import { useBangKanban, useDiChuyenItem, useNgayDangTheoMuc, useYTuong } from '../api.js';
import { CotKanban } from '../components/CotKanban.js';
import { DialogVideoMoi } from '../components/DialogVideoMoi.js';
import { DrawerChiTiet } from '../components/DrawerChiTiet.js';
import { TheGoiY } from '../components/TheGoiY.js';
import { TheVideo } from '../components/TheVideo.js';

/** Lỗi 422 trả `details` là mảng `StageViolation`. */
function lyDoViPham(details: unknown): StageViolation[] {
  return Array.isArray(details) ? (details as StageViolation[]) : [];
}

export function TrangSanXuat() {
  const duongDan = useDuongDanTrang('NỘI DUNG');
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'production', 'edit');

  const [channelId, setChannelId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [dangMo, setDangMo] = useState<string | null>(null);
  const [moTaoMoi, setMoTaoMoi] = useState(false);
  const [dangKeo, setDangKeo] = useState<ProductionItem | null>(null);
  const [canhBao, setCanhBao] = useState<StageViolation[] | null>(null);

  const loc = {
    ...(channelId ? { channelId } : {}),
    ...(assigneeId ? { assigneeId } : {}),
  };

  const bang = useBangKanban(loc);
  const yTuong = useYTuong();
  const diChuyen = useDiChuyenItem();
  // Thẻ đã lên lịch hiện đúng ngày đăng trên lịch, không hiện `dueDate`.
  const ngayDang = useNgayDangTheoMuc(coQuyen(toi.data?.permissions, 'schedule', 'view'));

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const tatCaThe = useMemo(
    () => bang.data?.columns.flatMap((c) => c.items) ?? [],
    [bang.data?.columns],
  );

  // Ô chọn kênh và người phụ trách dựng từ chính dữ liệu bảng, không cần quyền
  // của module khác.
  const kenh = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tatCaThe) map.set(t.channelId, t.channelName);
    return [...map].map(([value, label]) => ({ value, label }));
  }, [tatCaThe]);

  const nguoi = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tatCaThe)
      if (t.assigneeId) map.set(t.assigneeId, t.assigneeName ?? t.assigneeId);
    return [...map].map(([value, label]) => ({ value, label }));
  }, [tatCaThe]);

  function batDauKeo(e: DragStartEvent) {
    setCanhBao(null);
    setDangKeo(tatCaThe.find((t) => t.id === e.active.id) ?? null);
  }

  function ketThucKeo(e: DragEndEvent) {
    const the = dangKeo;
    setDangKeo(null);
    if (!the || !e.over) return;

    const duLieu = e.over.data.current as { stage?: ProductionStage } | undefined;
    const stage = duLieu?.stage ?? the.stage;

    const cot = bang.data?.columns.find((c) => c.stage === stage);
    const viTri = cot ? cot.items.findIndex((i) => i.id === e.over?.id) : -1;
    const position = viTri >= 0 ? viTri : (cot?.items.length ?? 0);

    if (stage === the.stage && position === the.position) return;

    diChuyen.mutate(
      { id: the.id, stage, position },
      {
        onError: (err) => {
          const ly = lyDoViPham(err.details);
          setCanhBao(ly.length > 0 ? ly : [{ code: 'checklist_incomplete', message: err.message }]);
        },
      },
    );
  }

  return (
    <>
      <PageHeader
        breadcrumb={duongDan}
        title="Sản xuất nội dung"
        phu={`${String(tatCaThe.length)} video đang trong quy trình`}
        giaiThich="Mỗi video đi qua 6 bước. Kéo thẻ sang cột kế tiếp để cập nhật trạng thái; bước nào thiếu điều kiện thì app chặn và nói rõ thiếu gì."
        actions={
          coSua ? (
            <Nut
              kieu="chinh"
              onClick={() => {
                setMoTaoMoi(true);
              }}
            >
              + Video mới
            </Nut>
          ) : undefined
        }
      />

      <KhoiChuaCoKenh tenTrang="Sản xuất nội dung" />

      {/*
        Hai ô lọc rời `PageHeader` xuống `ToolbarBang`: đầu trang chỉ còn tiêu
        đề và **một** nút primary, theo luật của bộ thành phần mới.
      */}
      <ToolbarBang
        trai={
          <>
            <OChon
              label="Kênh"
              value={channelId}
              onChange={setChannelId}
              options={[{ value: '', label: 'Tất cả kênh' }, ...kenh]}
            />
            <OChon
              label="Người phụ trách"
              value={assigneeId}
              onChange={setAssigneeId}
              options={[{ value: '', label: 'Tất cả người phụ trách' }, ...nguoi]}
            />
          </>
        }
      />

      {canhBao && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 rounded-the border border-vien bg-bad-nen p-4 text-co-ban text-bad-chu"
        >
          <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold">Chưa đủ điều kiện để chuyển bước</p>
            <ul className="mt-1 list-inside list-disc">
              {canhBao.map((v) => (
                <li key={v.code}>{v.message}</li>
              ))}
            </ul>
          </div>
          <Nut
            kieu="nhe"
            className="ml-auto h-8 min-h-0 px-2 text-phu"
            onClick={() => {
              setCanhBao(null);
            }}
          >
            Đóng
          </Nut>
        </div>
      )}

      {bang.isPending && (
        <div className="flex gap-4 overflow-x-auto">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="h-64 w-[280px] shrink-0 animate-pulse rounded-the border border-vien bg-be-mat"
            />
          ))}
        </div>
      )}

      {bang.isError && (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được bảng sản xuất"
            description={bang.error.message}
            action={
              <Nut
                onClick={() => {
                  void bang.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        </div>
      )}

      {bang.data && tatCaThe.length === 0 && (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            icon={PenLine}
            title="Chưa có video nào"
            description="Tạo thẻ đầu tiên để bắt đầu theo dõi quy trình sản xuất."
            action={
              coSua ? (
                <Nut
                  kieu="chinh"
                  onClick={() => {
                    setMoTaoMoi(true);
                  }}
                >
                  + Video mới
                </Nut>
              ) : undefined
            }
          />
        </div>
      )}

      {bang.data && tatCaThe.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={batDauKeo}
          onDragEnd={ketThucKeo}
          onDragCancel={() => {
            setDangKeo(null);
          }}
        >
          {/*
            Bảng rộng thì cuộn ngang trong khung riêng, không đẩy cả trang.

            `relative` là **bắt buộc**: thẻ video có nhãn `sr-only`, tức
            `position: absolute`. Không có khối chứa định vị trong khung cuộn,
            chúng neo vào gốc trang ở toạ độ x của cột tương ứng — cột thứ năm
            nằm ở 1.226px — nên `scrollWidth` của cả trang phình lên 1.522px và
            trang cuộn ngang dù bảng kanban đã có khung cuộn riêng. Đúng cái bẫy
            `DataTable` đã gặp với `caption`; xem `e2e/91-khong-tran-ngang`.
          */}
          <div className="relative overflow-x-auto pb-2">
            <div className="flex min-w-max gap-4">
              {bang.data.columns.map((cot) => (
                <CotKanban
                  key={cot.stage}
                  cot={cot}
                  ngayDang={ngayDang}
                  onMo={(id) => {
                    setDangMo(id);
                  }}
                />
              ))}
            </div>
          </div>

          <DragOverlay>
            {dangKeo && (
              <div className="w-[256px]">
                <TheVideo tinh item={dangKeo} onMo={() => undefined} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      <TheGoiY yTuong={yTuong.data ?? []} />

      {dangMo && (
        <DrawerChiTiet
          itemId={dangMo}
          coSua={coSua}
          onDong={() => {
            setDangMo(null);
          }}
        />
      )}

      {moTaoMoi && (
        <DialogVideoMoi
          kenh={kenh}
          nguoiPhuTrach={nguoi}
          onDong={() => {
            setMoTaoMoi(false);
          }}
        />
      )}
    </>
  );
}
